import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";
import { IdentityService } from "../services/identity";

interface HeroSectionProps {
  onReviewClick: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onReviewClick }) => {
  const { user, userOnboarded, loading } = useAuth();
  const [isUserReviewer, setIsUserReviewer] = useState(false);
  const [isCheckingReviewerStatus, setIsCheckingReviewerStatus] =
    useState(true);

  useEffect(() => {
    const checkReviewerStatus = async () => {
      if (user?.id) {
        setIsCheckingReviewerStatus(true);
        const identity = await IdentityService.getUserIdentity(user.id);
        const hasReviewerRole = identity.isReviewer;
        setIsUserReviewer(hasReviewerRole);
        setIsCheckingReviewerStatus(false);
      } else {
        setIsUserReviewer(false);
        setIsCheckingReviewerStatus(false);
      }
    };

    checkReviewerStatus();
  }, [user?.id, userOnboarded]);

  const handleReviewClick = () => {
    if (user) {
      if (userOnboarded === false) {
        window.location.href = "/onboarding";
      } else {
        window.location.href = isUserReviewer ? "/reviewer-dashboard" : "/applicant-dashboard";
      }
    } else {
      onReviewClick();
    }
  };

  const getButtonText = () => {
    if (!user) return "Start Your Application Journey →";
    if (userOnboarded === false) return "Complete Onboarding →";
    if (isUserReviewer) return "Return to Dashboard →";
    return "Continue Your Journey →";
  };

  const placeholderLabel = "Start Your Application Journey →"; // Longest label to reserve space

  return (
    <>
      <Helmet>
        <title>
          CampusQuest - AI-Powered College Essay Review & Writing Assistant
        </title>
        <meta
          name="description"
          content="Get comprehensive AI-powered guidance throughout your entire college application journey. From essay review to deadline tracking and brainstorming tools."
        />
        <link rel="canonical" href="https://campusquest.io/" />
        <meta
          property="og:title"
          content="CampusQuest - Your AI Copilot for College Applications"
        />
        <meta
          property="og:description"
          content="Get comprehensive AI-powered guidance throughout your entire college application journey. From essay review to deadline tracking and brainstorming tools."
        />
        <meta property="og:url" content="https://campusquest.io/" />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-gray-200 shadow-sm mb-8">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            <span className="text-sm font-medium text-gray-900">
              Trusted by 10,000+ students
            </span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-semibold tracking-tight text-gray-900 mb-6 leading-tight">
            Your AI Copilot for College Applications
          </h1>
          <p className="text-base lg:text-lg text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed">
            From essay review to deadline tracking, get comprehensive AI-powered
            guidance throughout your entire college application journey.
          </p>
          <div className="flex justify-center gap-4">
            <Button
              onClick={handleReviewClick}
              size="lg"
              className="bg-black text-white px-6 py-3 text-base md:px-10 md:py-4 md:text-lg font-medium hover:bg-gray-800 shadow-sm hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
              disabled={loading || isCheckingReviewerStatus}
            >
              {loading || isCheckingReviewerStatus ? (
                <span className="invisible">{placeholderLabel}</span>
              ) : (
                getButtonText()
              )}
            </Button>
          </div>
          <p className="mt-6 text-sm text-gray-500">
            AI-powered essay feedback, deadline management, brainstorming tools,
            and more.
          </p>
        </div>
      </div>
    </>
  );
};

export default HeroSection;
